import { formatCopy } from '../copy/format.js';
import copy from '../copy/en/signing.json' with { type: 'json' };
import additionalOfficeCopy from '../copy/en/additional-office.json' with { type: 'json' };
import { CONSENT_TEXT, isNativeIntakeProfileVersion } from "@ohf/core/shared";
import { configureNativeIntake, collectNativeIntake, prefillNativeIntake, disposeNativeIntake, nativeStageErrors, nativeErrorControl, renderNativeReview, type NativeSignerPayload } from './native-intake.js';
import { createNativeDraftController } from './native-draft-controller.js';
import { createAdditionalOfficeFlow, officeSignerKey } from './additional-office.js';
import { initializeCarriedFieldEditing, type CarriedFieldEditing } from './carried-field-editing.js';
import { initPracticeControls, syncPracticeControls } from "./controls.js";
import {
  attachMask,
  formatPhone,
  formatZip,
  type MaskController,
} from "./masks.js";
import { isMockRequested, fillMockData } from "./mock.js";
import { resolveResumeToken, resumeErrorMessage } from "./resume.js";
import { renderReview } from "./review.js";
import { createReviewReturn } from './review-return.js';
import { createPracticeSignature } from "./practice-signature.js";
import { initializeFormFallback } from './form-fallback.js';
import { initializeSignatureCapabilities } from './signature-capabilities.js';
import { signingRetryGuidance } from "./retry-guidance.js";
import { initializeSignerEmbed } from './signer-embed.js';
import { initializeNavigationPreview, isNavigationPreviewRequested } from './navigation-preview.js';
import {
  DATA_STAGES,
  JOURNEY_STEPS,
  clampDataStage,
  firstErrorStage,
} from "./flow.js";

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector(selector) as T;

const disposeSignerEmbed = initializeSignerEmbed();
let disposeFormFallback = initializeFormFallback(() => !busy && !complete && !signature?.pending);
let disposeSignatureCapabilities = initializeSignatureCapabilities();

function resolveFormKey(): string {
  const match = window.location.pathname.match(/\/forms\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : "agreement";
}

function resolveBundleToken(): string {
  return new URLSearchParams(window.location.search).get("bundle") ?? "";
}

function resolveLinkCode(): string {
  return new URLSearchParams(window.location.search).get("lc") ?? "";
}
function resolveInvitationCode(): string {
  return new URLSearchParams(window.location.search).get('invitation') ?? '';
}

const formKey = resolveFormKey();
const navigationPreview = isNavigationPreviewRequested(window.location.search, formKey);
const apiUrl = (action: string): string =>
  `/api/forms/${encodeURIComponent(formKey)}/${action}`;
const bundleToken = navigationPreview ? "" : resolveBundleToken();
const resumeToken = navigationPreview ? "" : resolveResumeToken(window.location.search);
const linkCode = navigationPreview ? "" : resolveLinkCode();
const invitationCode = navigationPreview ? "" : resolveInvitationCode();

const form = $<HTMLFormElement>("#signerForm");
const dataStageElements = Array.from(
  form.querySelectorAll<HTMLElement>("[data-data-stage]"),
);
const progressRail = $<HTMLElement>(".progress-rail");
const progressCount = $<HTMLElement>("#progressCount");
const previewBtn = $<HTMLButtonElement>("#previewBtn");
const reviewSection = $<HTMLElement>("#reviewSection");
const pdfFrame = $<HTMLIFrameElement>("#pdfFrame");
const consentCheck = $<HTMLInputElement>("#consentCheck");
const signBlock = $<HTMLElement>("#signBlock");
const finalizeBtn = $<HTMLButtonElement>("#finalizeBtn");
const successSection = $<HTMLElement>("#successSection");
const formError = $<HTMLElement>("#formError");
const finalizeError = $<HTMLElement>("#finalizeError");
const typedNameInput = $<HTMLInputElement>("#typedName");
const intakeFields = $<HTMLFieldSetElement>("#intakeFields");
const reviewFields = $<HTMLFieldSetElement>("#reviewFields");
const sessionStatus = $("#sessionStatus");
const sessionError = $("#sessionError");
const retrySession = $<HTMLButtonElement>("#retrySession");
const recoverDocument = $<HTMLButtonElement>("#recoverDocument");
const copyResumeLink = $<HTMLButtonElement>("#copyResumeLink");

let currentDataStage = 0;
let signingToken = "";
const sessionTokens = new Set<string>();
let signature: ReturnType<typeof createPracticeSignature> | null = null;
let lastDownloadUrl = "";
const completedDownloadUrls = new Set<string>();
let previewUrl = "";
let ready = false;
let busy = false;
let complete = false;
let reviewedData: NativeSignerPayload | null = null;
let nativeIntake = false;
let reviewReturn: ReturnType<typeof createReviewReturn> | null = null;
let nativeDraft: Awaited<ReturnType<typeof createNativeDraftController>> | null = null;
let additionalOffice: ReturnType<typeof createAdditionalOfficeFlow> | null = null;
let carriedFieldEditing: CarriedFieldEditing | null = null;
let carriedSignerIdentity = "";
let disposed = false;
const draftLifecycle = new AbortController();
let savedFingerprint = "";
let enteredFingerprint = "";
let copyGeneration = 0;
const focusTimers = new Set<number>();

function setBusy(value: boolean): void {
  busy = value;
  reviewReturn?.setBusy(value);
  additionalOffice?.setBusy(value);
  intakeFields.disabled = value || !ready || complete;
  reviewFields.disabled = value || !ready || complete;
  retrySession.disabled = value;
  recoverDocument.disabled = value;
  copyResumeLink.disabled = value;
  $(".form-surface").setAttribute("aria-busy", String(value));
  signature?.setEnabled(!value && ready && consentCheck.checked && !complete);
}

async function post(action: string, data: Record<string, unknown>): Promise<Response> {
  if (navigationPreview) throw new Error(copy.navigationPreviewSigningDisabled);
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 30_000);
  try {
    return await fetch(apiUrl(action), {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data), signal: controller.signal, cache: "no-store",
    });
  } finally { window.clearTimeout(timer); }
}

async function readPdf(response: Response): Promise<Blob> {
  if (!response.headers.get("Content-Type")?.toLowerCase().includes("application/pdf")) {
    throw new Error("Document response was not a PDF");
  }
  const bytes = await response.blob();
  if (!bytes.size) throw new Error("Document response was empty");
  return bytes;
}

$("#consentText").textContent = CONSENT_TEXT;

const phoneInput = form.elements.namedItem("phone") as HTMLInputElement;
const mobileInput = form.elements.namedItem("mobilePhone") as HTMLInputElement;
const zipInput = form.elements.namedItem("zip") as HTMLInputElement;
const phoneMask: MaskController = attachMask(phoneInput, formatPhone, 10);
const mobileMask: MaskController = attachMask(mobileInput, formatPhone, 10);
attachMask(zipInput, formatZip, 9);
initPracticeControls(form);

if (!navigationPreview && formKey === "agreement" && !resumeToken && isMockRequested(window.location.search)) {
  fillMockData(form);
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function revealAndFocus(section: HTMLElement, headingSelector: string): void {
  section.scrollIntoView({
    behavior: prefersReducedMotion() ? "auto" : "smooth",
    block: "start",
  });
  const timer = window.setTimeout(() => {
    focusTimers.delete(timer);
    section.querySelector<HTMLElement>(headingSelector)?.focus();
  }, 0);
  focusTimers.add(timer);
}

function setJourneyStep(stepIndex: number): void {
  const bounded = Math.min(JOURNEY_STEPS.length - 1, Math.max(0, stepIndex));
  JOURNEY_STEPS.forEach((_step, index) => {
    const item = document.querySelector<HTMLElement>(`#journey-step-${index + 1}`);
    if (!item) return;
    const active = index === bounded;
    item.classList.toggle("active", active);
    item.classList.toggle("done", index < bounded);
    if (active) item.setAttribute("aria-current", "step");
    else item.removeAttribute("aria-current");
  });
  progressCount.textContent = formatCopy(copy.progressCount, { current: bounded + 1, total: JOURNEY_STEPS.length });
  progressRail.style.setProperty(
    "--progress",
    String(bounded / (JOURNEY_STEPS.length - 1)),
  );
}

function showDataStage(index: number, focus = true): void {
  currentDataStage = clampDataStage(index);
  dataStageElements.forEach((stage, stageIndex) => {
    stage.classList.toggle("hidden", stageIndex !== currentDataStage);
  });
  form.classList.remove("hidden");
  reviewSection.classList.add("hidden");
  successSection.classList.add("hidden");
  setJourneyStep(currentDataStage);
  const active = dataStageElements[currentDataStage];
  if (focus && active) revealAndFocus(active, "h2");
  nativeDraft?.stageChanged();
}

function collectData(): NativeSignerPayload {
  carriedFieldEditing?.settle();
  if (nativeIntake) return collectNativeIntake(form);
  const fields = new FormData(form);
  const data: Record<string, string> = {};
  fields.forEach((value, key) => {
    data[key] = String(value);
  });
  data.phone = phoneMask.getRaw();
  data.mobilePhone = mobileMask.getRaw();
  return data as NativeSignerPayload;
}

function clearErrors(): void {
  document.querySelectorAll<HTMLElement>(".err").forEach((element) => {
    element.textContent = "";
  });
  document
    .querySelectorAll<HTMLElement>(".field.invalid, .seg.invalid, .chip-field.invalid, .field-group.invalid")
    .forEach((element) => element.classList.remove("invalid"));
  document.querySelectorAll<HTMLElement>("[data-stage-error]").forEach((element) => {
    element.textContent = "";
  });
  formError.textContent = "";
  finalizeError.textContent = "";
  document.querySelectorAll("[aria-invalid=true]").forEach((element) => element.removeAttribute("aria-invalid"));
}

function fieldContainer(name: string): HTMLElement | null {
  const input = nativeIntake && nativeErrorControl(form, name) || form.querySelector<HTMLElement>(`[name="${name}"]`);
  return input?.closest<HTMLElement>(".field, .seg, .chip-field, .field-group") ?? null;
}

function revealAncestorDisclosures(element: Element): void {
  let disclosure = element.closest<HTMLDetailsElement>("details");
  while (disclosure) {
    disclosure.open = true;
    disclosure = disclosure.parentElement?.closest<HTMLDetailsElement>("details") ?? null;
  }
}

function revealInvalidControl(event: Event): void {
  if (event.target instanceof Element) revealAncestorDisclosures(event.target);
}

function showFieldErrors(fields: Record<string, string>): void {
  for (const [name, message] of Object.entries(fields)) {
    const error = document.querySelector<HTMLElement>(`[data-err="${name}"]`);
    if (error) {
      error.textContent = message;
      error.id ||= `error-${name}`;
    }
    const container = fieldContainer(name);
    container?.classList.add("invalid");
    if (container) revealAncestorDisclosures(container);
    // A server error must reveal a conditional Other field before moving focus.
    container?.closest<HTMLElement>("[data-other-for]")?.classList.remove("hidden");
    const controls = form.querySelectorAll<HTMLElement>(`[name="${name}"]:not([type=hidden]), [data-chips="${name}"] button`);
    const nativeTarget = nativeIntake ? nativeErrorControl(form, name) : undefined;
    [...controls, ...(nativeTarget ? [nativeTarget] : [])].forEach((control) => {
      control.setAttribute("aria-invalid", "true");
      if (error) {
        const ids = new Set((control.getAttribute("aria-describedby") ?? "").split(/\s+/).filter(Boolean));
        ids.add(error.id);
        control.setAttribute("aria-describedby", [...ids].join(" "));
      }
    });
  }
}

function activeStageError(): HTMLElement | null {
  return dataStageElements[currentDataStage]?.querySelector<HTMLElement>(
    "[data-stage-error]",
  ) ?? null;
}

function setActiveStageError(message: string): void {
  const error = activeStageError();
  if (error) error.textContent = message;
}

function clientErrorFor(control: HTMLInputElement | HTMLSelectElement): string {
  if (control.validity.valueMissing) return copy.thisFieldIsRequired;
  if (control.validity.typeMismatch && control.type === "email") {
    return copy.enterAValidEmailAddress;
  }
  if (control.validity.rangeUnderflow && control instanceof HTMLInputElement) {
    return formatCopy(copy.minimumValue, { minimum: control.min });
  }
  if (control.validity.rangeOverflow && control instanceof HTMLInputElement) {
    return formatCopy(copy.maximumValue, { maximum: control.max });
  }
  if (control.validity.stepMismatch) return copy.enterAWholeNumber;
  return copy.checkThisFieldAndTryAgain;
}

function validateDataStage(stage: HTMLElement): boolean {
  carriedFieldEditing?.settle();
  clearErrors();
  const errors: Record<string, string> = {};
  // Synchronize custom-control validity before consulting browser constraints.
  const nativeErrors = nativeIntake ? nativeStageErrors(stage) : {};
  const handledRadios = new Set<string>();

  stage
    .querySelectorAll<HTMLInputElement | HTMLSelectElement>("input:not([type=hidden]), select")
    .forEach((control) => {
      if (!control.name || control.disabled) return;
      if (control instanceof HTMLInputElement && control.type === "radio") {
        if (handledRadios.has(control.name)) return;
        handledRadios.add(control.name);
        const group = form.elements.namedItem(control.name);
        if (control.required && group instanceof RadioNodeList && !group.value) {
          errors[control.name] = copy.chooseOneOption;
        }
        return;
      }
      if (!control.checkValidity()) errors[control.name] = clientErrorFor(control);
    });

  const days = stage.querySelector<HTMLInputElement>('[name="daysOpen"]');
  if (days && !days.value.trim()) errors.daysOpen = copy.selectAtLeastOneDay;
  Object.assign(errors, nativeErrors);

  if (Object.keys(errors).length === 0) return true;
  showFieldErrors(errors);
  setActiveStageError(copy.completeTheHighlightedFieldsToContinue);
  const firstInvalid = stage.querySelector<HTMLElement>("[aria-invalid=true]:not([type=hidden])") ?? stage.querySelector<HTMLElement>(".invalid input:not([type=hidden]), .invalid select, .invalid button");
  if (firstInvalid) revealAncestorDisclosures(firstInvalid);
  firstInvalid?.focus();
  return false;
}

function routeFieldErrors(fields: Record<string, string>, message: string): void {
  resetConfirmation();
  showDataStage(firstErrorStage(Object.keys(fields)), false);
  showFieldErrors(fields);
  setActiveStageError(message);
  const timer = window.setTimeout(() => {
    focusTimers.delete(timer);
    dataStageElements[currentDataStage]?.querySelector<HTMLElement>("[aria-invalid=true]:not([type=hidden])")?.focus();
  }, 0);
  focusTimers.add(timer);
}

function resetConfirmation(): void {
  clearSigningErrors();
  consentCheck.checked = false;
  signBlock.classList.add("hidden");
  const retainSignature = additionalOffice?.active && carriedSignerIdentity && currentSignerIdentity() === carriedSignerIdentity;
  if (!retainSignature) {
    signature?.clear();
    typedNameInput.value = "";
    carriedSignerIdentity = "";
  }
  signature?.setEnabled(false);
  $("#expectedSignerName").textContent = "";
  reviewedData = null;
}

function currentSignerIdentity(): string {
  return officeSignerKey({
    signerName: (form.elements.namedItem('signerName') as HTMLInputElement | null)?.value ?? '',
    email: (form.elements.namedItem('email') as HTMLInputElement | null)?.value ?? '',
  } as NativeSignerPayload);
}

function acceptsOfficeAddress(payload: NativeSignerPayload): boolean {
  if (!additionalOffice || additionalOffice.acceptsAddress(payload)) return true;
  showDataStage(0, false);
  additionalOffice.focusWarning();
  return false;
}

function editStage(index: number): void {
  if (busy || !ready || complete) return;
  clearErrors();
  resetConfirmation();
  reviewReturn?.setActive(true);
  showDataStage(index);
  sessionStatus.textContent = nativeIntake ? copy.nativeEditingAnswers : copy.editingAnswersGenerateTheAgreementAgainTo;
}

function showSessionProblem(reason: unknown, fallback: string): boolean {
  const messages: Record<string, string> = {
    expired: copy.thisSigningSessionHasExpiredAskOHF,
    wrong_form: copy.thisSigningSessionBelongsToAnotherForm,
    unknown: copy.thisSigningSessionIsNoLongerAvailable,
    document_changed: copy.theAgreementVersionHasChangedAskOHF,
    not_next_in_sequence: copy.thisIsNotTheNextDocumentIn,
    prior_not_finalized: copy.completeThePrecedingDocumentInYourBundle,
    bundle_completed: copy.thisBundleIsAlreadyCompleteUseThe,
  };
  if (typeof reason !== "string" || !messages[reason]) return false;
  ready = false;
  resetConfirmation();
  form.classList.add("hidden");
  reviewSection.classList.add("hidden");
  retrySession.classList.add("hidden");
  recoverDocument.classList.add("hidden");
  sessionStatus.textContent = copy.thisSigningSessionNeedsAttention;
  sessionError.textContent = messages[reason] || fallback;
  sessionError.focus();
  return true;
}

function setButtonLabel(button: HTMLButtonElement, text: string): void {
  const label = button.querySelector<HTMLElement>(".btn-label");
  if (label) label.textContent = text;
  else button.textContent = text;
}

form.addEventListener("click", (event) => {
  if (busy || !ready || complete) return;
  const target = event.target as HTMLElement;
  const next = target.closest<HTMLButtonElement>("[data-stage-next]");
  if (next && form.contains(next)) {
    const stage = dataStageElements[currentDataStage];
    if (stage && validateDataStage(stage)) showDataStage(currentDataStage + 1);
    return;
  }
  const back = target.closest<HTMLButtonElement>("[data-stage-back]");
  if (back && form.contains(back)) {
    clearErrors();
    showDataStage(currentDataStage - 1);
  }
});
form.addEventListener("invalid", revealInvalidControl, true);

form.addEventListener("submit", async (event) => {
  event.preventDefault();
  if (busy || !ready || complete) return;
  if (!reviewReturn?.active && currentDataStage !== DATA_STAGES.length - 1) {
    const stage = dataStageElements[currentDataStage];
    if (stage && validateDataStage(stage)) showDataStage(currentDataStage + 1);
    return;
  }

  const returningToReview = reviewReturn?.active ?? false;
  const editedStage = currentDataStage;
  if (nativeIntake || returningToReview) {
    for (const [index, stage] of dataStageElements.entries()) {
      if (!validateDataStage(stage)) {
        showDataStage(index, false);
        setActiveStageError(copy.completeTheHighlightedFieldsToContinue);
        stage.querySelector<HTMLElement>('[aria-invalid=true]:not([type=hidden])')?.focus();
        return;
      }
    }
  } else {
    const stage = dataStageElements[currentDataStage];
    if (!stage || !validateDataStage(stage)) return;
  }

  const payload = collectData();
  if (!acceptsOfficeAddress(payload)) return;
  nativeDraft?.flush();
  setBusy(true);
  setButtonLabel(previewBtn, copy.generatingAgreement);
  try {
    const response = await post("preview", { ...payload, signingToken });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      if (showSessionProblem(error.reason, error.error)) return;
      if (error.reason === "already_signed") { offerRecovery(true); return; }
      if (error.fields) {
        const fields = error.fields as Record<string, string>;
        routeFieldErrors(fields, copy.checkTheHighlightedAnswersThenGenerateThe);
      } else {
        setActiveStageError(signingRetryGuidance(response, error.error ?? copy.couldNotGenerateTheAgreement));
      }
      return;
    }

    const bytes = await readPdf(response);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = URL.createObjectURL(bytes);
    pdfFrame.src = previewUrl;
    $<HTMLAnchorElement>("#openPreview").href = previewUrl;
    $<HTMLAnchorElement>("#downloadPreview").href = previewUrl;
    reviewedData = { ...payload };
    $("#expectedSignerName").textContent = payload.signerName?.trim() ?? "";
    savedFingerprint = JSON.stringify(payload);
    enteredFingerprint = savedFingerprint;
    nativeDraft?.reviewed(payload);
    if (nativeIntake) renderNativeReview($("#reviewAnswers"), payload);
    else renderReview($("#reviewAnswers"), form, payload as Record<string, string>);
    $("#resumeActions").classList.remove("hidden");
    sessionStatus.textContent = copy.yourAnswersAreSavedYouCanCopy;
    form.classList.add("hidden");
    reviewSection.classList.remove("hidden");
    reviewReturn?.setActive(false);
    setJourneyStep(DATA_STAGES.length);
    const editedCard = returningToReview ? reviewSection.querySelector<HTMLDetailsElement>(`[data-review-stage="${editedStage}"]`) : null;
    if (editedCard) editedCard.open = true;
    revealAndFocus(editedCard ?? reviewSection, editedCard ? 'summary' : 'h2');
  } catch {
    setActiveStageError(copy.couldNotLoadTheAgreementYourAnswers);
  } finally {
    setBusy(false);
    setButtonLabel(previewBtn, copy.generateAgreement);
  }
});

$("#editFormBtn").addEventListener("click", () => editStage(0));
$("#editSignerDetails").addEventListener("click", () => {
  if (busy || !ready || complete || !reviewedData) return;
  editStage(1);
  revealAndFocus(dataStageElements[1]!, '[name="signerName"]');
});
$("#reviewAnswers").addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-edit-stage]");
  if (button) editStage(Number(button.dataset.editStage));
});
reviewSection.addEventListener("click", (event) => {
  if (busy && (event.target as HTMLElement).closest("a")) event.preventDefault();
});

consentCheck.addEventListener("change", () => {
  if (busy || !ready || complete || !reviewedData) return;
  $("#consentError").textContent = "";
  consentCheck.removeAttribute("aria-invalid");
  signBlock.classList.toggle("hidden", !consentCheck.checked);
  if (!consentCheck.checked) {
    setJourneyStep(DATA_STAGES.length);
    signature?.setEnabled(false);
    return;
  }
  setJourneyStep(DATA_STAGES.length + 1);
  initSignaturePad();
  revealAndFocus(signBlock, "#signatureTitle");
});

function initSignaturePad(): void {
  if (navigationPreview) return;
  if (additionalOffice?.active && reviewedData) carriedSignerIdentity = officeSignerKey(reviewedData);
  signature ??= createPracticeSignature($<HTMLCanvasElement>("#sigPad"), () => {
    $("#signatureError").textContent = "";
    $("#sigPad").removeAttribute("aria-invalid");
  });
  signature.resize();
  signature.setEnabled(true);
}
$("#clearSig").addEventListener("click", () => {
  if (busy || complete) return;
  signature?.clear();
  $("#signatureError").textContent = "";
  $("#sigPad").removeAttribute("aria-invalid");
  $("#sigPad").focus();
});

function clearSigningErrors(): void {
  finalizeError.textContent = "";
  for (const selector of ['#consentError', '#signatureError', '#error-typedName']) $(selector).textContent = "";
  for (const selector of ['#consentCheck', '#sigPad', '#typedName']) $(selector).removeAttribute("aria-invalid");
  typedNameInput.closest(".field")?.classList.remove("invalid");
}

function signingError(control: HTMLElement, error: HTMLElement, message: string): void {
  error.textContent = message;
  control.setAttribute("aria-invalid", "true");
  control.closest(".field")?.classList.add("invalid");
  control.scrollIntoView({ behavior: "auto", block: "center" });
  control.focus({ preventScroll: true });
}

typedNameInput.addEventListener("input", () => {
  if (busy || complete) return;
  typedNameInput.closest(".field")?.classList.remove("invalid");
  typedNameInput.removeAttribute("aria-invalid");
  $("#error-typedName").textContent = "";
});
$("#sigPad").addEventListener("pointerdown", () => {
  if (busy || complete || !consentCheck.checked || !signature) return;
  $("#signatureError").textContent = "";
  $("#sigPad").removeAttribute("aria-invalid");
});

finalizeBtn.addEventListener("click", async () => {
  if (busy || !ready || complete || !reviewedData || signature?.pending) return;
  clearSigningErrors();

  const payload = { ...reviewedData };
  if (!acceptsOfficeAddress(payload)) return;
  const signerName = payload.signerName?.trim() ?? "";
  const typedName = typedNameInput.value.trim();
  if (!consentCheck.checked) {
    signingError(consentCheck, $("#consentError"), copy.acceptTheElectronicConsentBeforeSigning);
    return;
  }
  if (!signature || signature.isEmpty()) {
    signingError($("#sigPad"), $("#signatureError"), copy.drawYourSignatureInTheBoxAbove);
    return;
  }
  if (!typedName || typedName.toLowerCase() !== signerName.toLowerCase()) {
    signingError(typedNameInput, $("#error-typedName"), formatCopy(
      typedName ? copy.typeYourLegalNameExactlyAsEntered : copy.enterYourLegalNameToConfirm,
      { name: signerName },
    ));
    return;
  }

  const signatureImage = signature.exportPng();
  setBusy(true);
  setButtonLabel(finalizeBtn, copy.finalizing);
  try {
    const response = await post("finalize", {
      ...payload,
      signingToken,
      consentAccepted: true,
      signatureImage,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      if (showSessionProblem(error.reason, error.error)) return;
      if (error.reason === "review_changed" || error.reason === "review_required") {
        resetConfirmation();
        recoverDocument.classList.add("hidden");
        sessionError.textContent = "";
        sessionStatus.textContent = copy.aFreshAgreementReviewIsNeededBefore;
        showDataStage(0);
        setActiveStageError(copy.yourReviewedAnswersAreNoLongerCurrent);
        return;
      }
      if (error.fields) {
        routeFieldErrors(error.fields as Record<string, string>, copy.checkTheHighlightedAnswersThenReviewThe);
      } else {
        finalizeError.textContent = signingRetryGuidance(response, error.error ?? copy.couldNotFinalizeTheDocumentYourSignature);
        if (error.reason === "already_signed") offerRecovery(true);
        else if (response.status >= 500 && error.reason !== "template_mismatch") offerRecovery();
      }
      return;
    }
    await showCompleted(response, payload.email, payload);
  } catch {
    finalizeError.textContent = copy.theConnectionWasInterruptedYourDocumentMay;
    offerRecovery();
  } finally {
    setBusy(false);
    setButtonLabel(finalizeBtn, copy.signAndSubmit);
  }
});

function offerRecovery(alreadySigned = false): void {
  if (alreadySigned) {
    disposeFormFallback();
    ready = false;
    resetConfirmation();
    form.classList.add("hidden");
    reviewSection.classList.add("hidden");
  }
  recoverDocument.classList.remove("hidden");
  sessionError.textContent = alreadySigned
    ? copy.thisDocumentHasAlreadyBeenSignedRetrieve
    : copy.notSureWhetherSigningFinishedCheckFor;
}

async function showCompleted(response: Response, signerEmail?: string, capturedAnswers: NativeSignerPayload | null = null): Promise<void> {
  const bytes = await readPdf(response);
  const submissionId = response.headers.get("X-Submission-Id") ?? "";
  const finalHash = response.headers.get("X-Final-Sha256") ?? "";
  if (!submissionId || !finalHash) throw new Error(copy.documentEvidenceWasMissing);
  const emailQueued = response.headers.get("X-Email-Queued") === "true";
  lastDownloadUrl = URL.createObjectURL(bytes);
  completedDownloadUrls.add(lastDownloadUrl);
  complete = true;
  disposeFormFallback();
  disposeSignatureCapabilities();
  nativeDraft?.complete();
  sessionError.textContent = "";
  sessionStatus.textContent = copy.signingCompleteKeepYourPrivateReturnLink;
  $("#resumeActions").classList.remove("hidden");
  recoverDocument.classList.add("hidden");
  retrySession.classList.add("hidden");
  $("#outSubmission").textContent = submissionId;
  $("#outHash").textContent = finalHash;
  const emailNote = $("#emailNote");
  emailNote.classList.toggle("hidden", !emailQueued);
  emailNote.textContent = emailQueued
    ? formatCopy(signerEmail ? copy.emailQueuedToRecipient : copy.emailQueued, { email: signerEmail?.trim() ?? "" })
    : "";
  const download = $<HTMLAnchorElement>("#downloadAgain");
  download.href = lastDownloadUrl;
  download.download = `signed-packet-${submissionId}.pdf`;
  const nextForm = response.headers.get("X-Bundle-Next-Form") ?? "";
  const nextBundleToken = response.headers.get("X-Bundle-Token") ?? "";
  additionalOffice?.completed(capturedAnswers, lastDownloadUrl, download.download,
    nativeIntake && formKey === 'agreement' && !resumeToken && !bundleToken && !linkCode
      && !nextForm && !nextBundleToken
      && !new URLSearchParams(window.location.search).has('revision'));
  if (nextForm && nextBundleToken) showBundleContinue(nextForm, nextBundleToken);
  form.classList.add("hidden");
  reviewSection.classList.add("hidden");
  successSection.classList.remove("hidden");
  setJourneyStep(JOURNEY_STEPS.length - 1);
  revealAndFocus(successSection, "h2");
}

/** Start a separate public application; no completed request credentials cross this boundary. */
async function startAdditionalOffice(payload: NativeSignerPayload): Promise<boolean> {
  if (navigationPreview || disposed || busy || !complete || !nativeIntake
    || formKey !== 'agreement' || resumeToken || bundleToken || linkCode) return false;
  setBusy(true);
  sessionError.textContent = '';
  let started = false;
  try {
    const response = await post('session', invitationCode ? { invitationCode } : {});
    const data = await response.json();
    if (disposed) return false;
    const version = data?.intakeProfileVersion;
    const token = typeof data?.signingToken === 'string' ? data.signingToken.trim() : '';
    const profile = payload.intakeProfile as { schemaVersion?: unknown } | undefined;
    if (!response.ok || !token || sessionTokens.has(token) || data.resumed
      || !isNativeIntakeProfileVersion(version) || profile?.schemaVersion !== version
      || form.dataset.intakeProfileVersion !== version) {
      sessionError.textContent = additionalOfficeCopy.startError;
      return false;
    }

    // Only after the fresh session is verified do we leave the prior completion.
    carriedFieldEditing?.dispose(); carriedFieldEditing = null;
    nativeDraft?.dispose(); nativeDraft = null;
    reviewReturn?.dispose();
    reviewReturn = createReviewReturn(form, copy.updateReview, copy.updatingReview);
    signingToken = token;
    sessionTokens.add(token);
    carriedSignerIdentity = officeSignerKey(payload);
    // The reviewed snapshot alone supplies the next office's answers.
    prefillNativeIntake(form, payload);
    resetConfirmation();
    complete = false;
    ready = true;
    savedFingerprint = '';
    enteredFingerprint = '';
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = '';
    pdfFrame.removeAttribute('src');
    $('#openPreview').removeAttribute('href');
    $('#downloadPreview').removeAttribute('href');
    $('#reviewAnswers').replaceChildren();
    $('#resumeActions').classList.add('hidden');
    $('#manualResumeCopy').classList.add('hidden');
    $<HTMLInputElement>('#resumeLinkValue').value = '';
    $('#resumeCopyStatus').textContent = '';
    copyGeneration++;
    $('#bundleContinue')?.classList.add('hidden');
    recoverDocument.classList.add('hidden');
    retrySession.classList.add('hidden');
    clearErrors();
    if (data.form?.consentText) $('#consentText').textContent = data.form.consentText;
    if (data.form?.title) document.title = data.form.title;
    disposeFormFallback();
    disposeFormFallback = initializeFormFallback(() => !busy && !complete && !signature?.pending);
    disposeSignatureCapabilities();
    disposeSignatureCapabilities = initializeSignatureCapabilities();
    intakeFields.disabled = false;
    carriedFieldEditing = initializeCarriedFieldEditing(form);
    enteredFingerprint = JSON.stringify(collectData());
    const controller = await createNativeDraftController({
      formKey, profileVersion: version,
      // The completed draft was cleared. Keep the public-link browser namespace
      // so reloading that same link can recover this new office's answers.
      context: { invitationCode, linkCode: '', resumeToken: '', bundleToken: '' },
      baseline: payload, preferServer: true,
      signal: draftLifecycle.signal, panel: $('#sessionPanel'),
      canCollect: () => ready && !busy && !complete,
      collect: collectData, restore: answers => prefillNativeIntake(form, answers),
      getStage: () => currentDataStage,
    });
    if (disposed) { controller.dispose(); return false; }
    nativeDraft = controller;
    sessionStatus.textContent = additionalOfficeCopy.sessionReady;
    showDataStage(0);
    started = true;
    return true;
  } catch {
    if (!disposed) sessionError.textContent = additionalOfficeCopy.startError;
    return false;
  } finally {
    if (!disposed) {
      setBusy(false);
      if (started) nativeDraft?.edit();
    }
  }
}

async function startSession(): Promise<void> {
  if (navigationPreview || busy || ready || complete) return;
  setBusy(true);
  sessionStatus.textContent = resumeToken ? copy.restoringYourSavedSigningSession : copy.openingYourSecureSigningSession;
  sessionError.textContent = "";
  retrySession.classList.add("hidden");
  try {
    const body: Record<string, string> = {};
    if (resumeToken) body.resumeToken = resumeToken;
    else if (bundleToken) body.bundleToken = bundleToken;
    if (!resumeToken && linkCode) body.linkCode = linkCode;
    if (!resumeToken && invitationCode) body.invitationCode = invitationCode;

    const response = await post("session", body);
    const data = await response.json();
    if (disposed) return;
    if (!response.ok) {
      sessionStatus.textContent = copy.yourSigningSessionCouldNotBeOpened;
      sessionError.textContent = resumeToken
        ? resumeErrorMessage(
            data?.reason,
            data?.error ?? copy.thisSigningLinkIsNoLongerValid,
          )
        : data?.reason === "prior_not_finalized"
          ? copy.pleaseCompleteThePreviousFormFirst
          : copy.thisLinkIsNoLongerValidPlease;
      if (response.status >= 500 || response.status === 429) {
        sessionError.textContent = signingRetryGuidance(response, copy.theSigningServiceIsTemporarilyUnavailableTry);
        retrySession.classList.remove("hidden");
      } else if (data?.reason === "already_signed" && resumeToken) {
        signingToken = resumeToken;
        recoverDocument.classList.remove("hidden");
        sessionError.textContent = copy.thisDocumentHasAlreadyBeenSignedYou;
      } else {
        sessionError.textContent += copy.pleaseUseTheLatestLinkFromOHF;
      }
      sessionError.focus();
      return;
    }
    if (typeof data.signingToken !== "string" || !data.signingToken) throw new Error("Invalid session response");
    signingToken = data.signingToken;
    sessionTokens.add(signingToken);
    if (data.intakeProfileVersion != null && !isNativeIntakeProfileVersion(data.intakeProfileVersion)) throw new Error('Unsupported intake profile');
    nativeIntake = isNativeIntakeProfileVersion(data.intakeProfileVersion);
    if (isNativeIntakeProfileVersion(data.intakeProfileVersion)) {
      carriedFieldEditing?.dispose(); carriedFieldEditing = null;
      configureNativeIntake(form, data.intakeProfileVersion);
      reviewReturn?.dispose();
      reviewReturn = createReviewReturn(form, copy.updateReview, copy.updatingReview);
      additionalOffice ??= createAdditionalOfficeFlow({
        form,
        onStart: startAdditionalOffice,
        onEditLocation: () => {
          if (busy || !ready || complete) return;
          if (!reviewSection.classList.contains('hidden')) {
            resetConfirmation();
            reviewReturn?.setActive(true);
          }
          showDataStage(0, false);
          const address = form.querySelector<HTMLInputElement>('[name="intakeProfile.locations.addressLine1"]');
          address?.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          address?.focus({ preventScroll: true });
        },
        onReview: () => {
          reviewReturn?.setActive(true);
          form.requestSubmit();
        },
      });
    }
    if (data.prefill) {
      if (nativeIntake) prefillNativeIntake(form, data.prefill as Record<string, unknown>);
      else applyPrefill(data.prefill as Record<string, string>);
    }
    await formInfoReady;
    if (disposed) return;
    // A retained session's consent and title override the current-form metadata.
    if (data.form?.consentText) $("#consentText").textContent = data.form.consentText;
    if (data.form?.title) document.title = data.form.title;
    ready = true;
    // FormData excludes disabled fieldsets. Establish the baseline only after enabling intake.
    intakeFields.disabled = false;
    enteredFingerprint = JSON.stringify(collectData());
    savedFingerprint = data.resumed && data.prefill ? enteredFingerprint : "";
    if (savedFingerprint) $("#resumeActions").classList.remove("hidden");
    sessionStatus.textContent = savedFingerprint
      ? copy.savedAnswersRestoredCheckYourInformationAnd
      : nativeIntake ? copy.nativeSessionReady : copy.yourSessionIsReadyAnswersAreSaved;
    if (nativeIntake && isNativeIntakeProfileVersion(data.intakeProfileVersion)) {
      const controller = await createNativeDraftController({
        formKey, profileVersion: data.intakeProfileVersion,
        context: { invitationCode, linkCode, resumeToken, bundleToken },
        baseline: collectData(), preferServer: Boolean(data.resumed || bundleToken && data.prefill),
        signal: draftLifecycle.signal,
        panel: $('#sessionPanel'), canCollect: () => ready && !busy && !complete,
        collect: collectData, restore: payload => prefillNativeIntake(form, payload), getStage: () => currentDataStage,
      });
      if (disposed) { controller.dispose(); return; }
      nativeDraft = controller;
      enteredFingerprint = JSON.stringify(collectData());
      for (const guidance of document.querySelectorAll<HTMLElement>('.hero > p:last-child, .embed-save-guidance')) guidance.textContent = copy.nativeBrowserSaveGuidance;
    }
    if (nativeIntake) additionalOffice?.changed(collectData());
    showDataStage(nativeDraft?.restoredStage ?? 0, false);
  } catch {
    if (disposed) return;
    sessionStatus.textContent = copy.yourSigningSessionCouldNotBeOpened;
    sessionError.textContent = copy.checkYourConnectionAndTryAgainYou;
    retrySession.classList.remove("hidden");
  } finally {
    if (!disposed) setBusy(false);
  }
}
retrySession.addEventListener("click", () => void startSession());
recoverDocument.addEventListener("click", async () => {
  if (navigationPreview || busy || !signingToken || complete) return;
  setBusy(true);
  sessionError.textContent = "";
  setButtonLabel(recoverDocument, copy.checkingSignedDocument);
  try {
    const response = await post("download", { signingToken });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      if (response.status === 410) {
        sessionError.textContent = copy.thisPrivateLinkHasExpiredAskOHF;
      } else if (response.status === 404 || error.reason === "wrong_form") {
        sessionError.textContent = copy.thisLinkCannotRetrieveTheDocumentPlease;
      } else if (error.reason === "not_finalized") {
        sessionError.textContent = copy.aCompletedDocumentIsNotAvailableYet;
      } else {
        sessionError.textContent = copy.theSignedDocumentCouldNotBeRetrieved;
      }
      sessionError.focus();
      return;
    }
    await showCompleted(response, reviewedData?.email);
  } catch {
    sessionError.textContent = copy.couldNotCheckTheDocumentCheckYour;
  } finally {
    setBusy(false);
    setButtonLabel(recoverDocument, copy.checkForSignedDocument);
  }
});

copyResumeLink.addEventListener("click", async () => {
  if (navigationPreview || busy || !signingToken) return;
  const generation = ++copyGeneration;
  const url = new URL(`/forms/${encodeURIComponent(formKey)}`, window.location.origin);
  url.searchParams.set("token", signingToken);
  try {
    await navigator.clipboard.writeText(url.href);
    if (generation !== copyGeneration) return;
    $("#resumeCopyStatus").textContent = copy.privateReturnLinkCopiedKeepItSomewhere;
    $("#manualResumeCopy").classList.add("hidden");
  } catch {
    if (generation !== copyGeneration) return;
    const input = $<HTMLInputElement>("#resumeLinkValue");
    input.value = url.href;
    $("#manualResumeCopy").classList.remove("hidden");
    input.focus();
    input.select();
    $("#resumeCopyStatus").textContent = copy.automaticCopyingIsUnavailableCopyTheSelected;
  }
});

function trackChanges(): void {
  if (!ready || busy || complete) return;
  const payload = collectData();
  resetConfirmation();
  additionalOffice?.changed(payload);
  enteredFingerprint = JSON.stringify(payload);
  nativeDraft?.edit();
}
form.addEventListener("input", trackChanges);
form.addEventListener("change", trackChanges);
function warnUnsaved(event: BeforeUnloadEvent): void {
  carriedFieldEditing?.settle();
  nativeDraft?.flush();
  const signingChanges = !!typedNameInput.value.trim() || !!signature && !signature.isEmpty() || consentCheck.checked;
  const browserSaved = nativeDraft && ready && !busy && !complete && nativeDraft.isSaved(collectData());
  if (!complete && ready && (busy || signingChanges || (!browserSaved && enteredFingerprint !== savedFingerprint &&
      Array.from(form.elements).some((element) =>
        element instanceof HTMLInputElement && element.type !== "hidden" && element.value.trim() &&
        (element.type !== "radio" || element.checked))))) {
    event.preventDefault();
    event.returnValue = "";
  }
}
window.addEventListener("beforeunload", warnUnsaved);

function applyPrefill(prefill: Record<string, string>): void {
  for (const [name, value] of Object.entries(prefill)) {
    if (!value) continue;
    const control = form.elements.namedItem(name);
    if (!control) continue;
    if (control instanceof RadioNodeList) {
      control.value = String(value);
      continue;
    }
    if (control instanceof HTMLInputElement || control instanceof HTMLSelectElement) {
      control.value = String(value);
      control.dispatchEvent(new Event("input", { bubbles: true }));
      control.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
  syncPracticeControls(form);
}

async function loadFormInfo(): Promise<void> {
  if (navigationPreview) return;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(apiUrl("info"), { signal: controller.signal, cache: "no-store" });
    if (!response.ok) return;
    const info = await response.json();
    if (info?.consentText) $("#consentText").textContent = info.consentText;
    if (info?.title) document.title = info.title;
  } catch {
    // The registered consent fallback remains visible when metadata is unavailable.
  } finally { window.clearTimeout(timer); }
}

function showBundleContinue(nextForm: string, token: string): void {
  let link = document.getElementById("bundleContinue") as HTMLAnchorElement | null;
  if (!link) {
    link = document.createElement("a");
    link.id = "bundleContinue";
    link.className = "btn btn-ghost";
    successSection.appendChild(link);
  }
  link.href = `/forms/${encodeURIComponent(nextForm)}?bundle=${encodeURIComponent(token)}`;
  link.textContent = copy.continueToTheNextForm;
  link.classList.remove("hidden");
}

/** Release page-owned listeners and Blob URLs (also used by executable DOM tests). */
export function disposeSignerJourney(): void {
  disposed = true;
  draftLifecycle.abort();
  carriedFieldEditing?.dispose();
  nativeDraft?.dispose();
  additionalOffice?.dispose();
  reviewReturn?.dispose();
  disposeNativeIntake(form);
  disposeNavigationPreview();
  disposeSignerEmbed();
  disposeFormFallback();
  disposeSignatureCapabilities();
  copyGeneration++;
  signature?.dispose();
  window.removeEventListener("beforeunload", warnUnsaved);
  form.removeEventListener("invalid", revealInvalidControl, true);
  focusTimers.forEach((timer) => window.clearTimeout(timer));
  if (previewUrl) URL.revokeObjectURL(previewUrl);
  completedDownloadUrls.forEach(url => URL.revokeObjectURL(url));
}

const formInfoReady = navigationPreview ? Promise.resolve() : loadFormInfo();
const disposeNavigationPreview = navigationPreview
  ? initializeNavigationPreview({ form, showDataStage, setJourneyStep, revealAndFocus })
  : () => {};
if (!navigationPreview) void startSession();
